import axios from "axios";
import * as CONSTANTS from "../utils/constants/constants";

// Notification types supported by backend
export type BillingNotificationType =
  | "monthly_summary"
  | "weekly_summary"
  | "billing_alert"
  | "payment_reminder"
  | "audit_report";

export interface ClinicRef {
  id: number;
  name?: string;
  organization?: string;
}

export interface BillingRecipient {
  id: number;
  clinic: ClinicRef; // response shape
  email: string;
  name: string;
  title?: string;
  notification_types: BillingNotificationType[];
  is_active: boolean;
  send_on_weekends: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
}

export interface BillingRecipientCreateRequest {
  clinic_id: number;
  email: string;
  name: string;
  title?: string;
  notification_types: BillingNotificationType[];
  is_active: boolean;
  send_on_weekends: boolean;
}

export interface BillingRecipientUpdateRequest extends BillingRecipientCreateRequest {}

export interface PaginatedRecipients {
  count: number;
  next: string | null;
  previous: string | null;
  results: BillingRecipient[];
}

export interface BillingSummarySendRequest {
  clinic_id: number;
  billing_month: string; // YYYY-MM
  notification_type: BillingNotificationType;
  recipient_emails?: string[]; // optional override
  organization_id?: string; // backend requires even if spec omitted
  month?: string; // backend validation currently expects 'month'
}

export interface BillingSummarySendResponse {
  status: string;
  message: string;
  details?: {
    total_recipients?: number;
    emails_sent?: number;
    emails_failed?: number;
    deliveries?: {
      recipient_email: string;
      status: string;
      delivery_id: number;
    }[];
  };
}

export interface DeliveryStatsResponse {
  summary: {
    total_deliveries: number;
    successful_deliveries: number;
    failed_deliveries: number;
    success_rate: number;
  };
  by_status: Record<string, number>;
  by_notification_type: Record<string, number>;
  recent_failures: {
    id: number;
    recipient_email: string;
    billing_month: string;
    status: string;
    error_message: string;
    created_at: string;
  }[];
}

export interface DeliveryHistoryEntry {
  id: number;
  recipient: {
    id: number;
    email: string;
    name: string;
  };
  billing_month: string;
  notification_type: BillingNotificationType;
  organization_id: string;
  status: string;
  created_at: string;
  sent_at?: string | null;
  delivered_at?: string | null;
  opened_at?: string | null;
  error_message: string;
  external_message_id?: string;
}

export interface DeliveryHistoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: DeliveryHistoryEntry[];
}

class BillingRecipientService {
  private static base = CONSTANTS.HOST + "api/v1/rpm";

  private static authHeaders(accessToken: string) {
    return { Authorization: `Bearer ${accessToken}` };
  }

  static async listRecipients(accessToken: string, params: Record<string, any> = {}): Promise<PaginatedRecipients> {
    const query = new URLSearchParams(params as any).toString();
    const url = `${this.base}/billing-recipients/${query ? "?" + query : ""}`;
    const headers = this.authHeaders(accessToken);
    console.log("[BillingRecipientService] REQUEST", { url, params });
    try {
      const response = await axios.get(url, { headers });
      let data = response.data;
      // Normalize shape: backend may return {recipients: [...], count: n}
      if (!data.results && Array.isArray(data.recipients)) {
        data = { ...data, results: data.recipients };
      }
      console.log("[BillingRecipientService] RESPONSE", {
        status: response.status,
        url,
        count: data?.count,
        keys: data ? Object.keys(data) : null,
        resultsLength: Array.isArray(data?.results) ? data.results.length : undefined,
        sampleFirst: Array.isArray(data?.results) ? data.results[0] : undefined,
      });
      return data;
    } catch (error: any) {
      console.error("[BillingRecipientService] ERROR", {
        url,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      throw error;
    }
  }

  static async createRecipient(accessToken: string, payload: BillingRecipientCreateRequest): Promise<BillingRecipient> {
    const url = `${this.base}/billing-recipients/`;
    const { data } = await axios.post(url, payload, { headers: this.authHeaders(accessToken) });
    return data;
  }

  static async updateRecipient(accessToken: string, id: number, payload: BillingRecipientUpdateRequest): Promise<BillingRecipient> {
    const url = `${this.base}/billing-recipients/${id}/`;
    const { data } = await axios.put(url, payload, { headers: this.authHeaders(accessToken) });
    return data;
  }

  static async deleteRecipient(accessToken: string, id: number): Promise<{ message: string }> {
    const url = `${this.base}/billing-recipients/${id}/`;
    const { data } = await axios.delete(url, { headers: this.authHeaders(accessToken) });
    return data;
  }

  static async sendBillingSummary(accessToken: string, payload: BillingSummarySendRequest): Promise<BillingSummarySendResponse> {
    const url = `${this.base}/send-billing-summary/`;
    console.log("[BillingRecipientService] SUMMARY REQUEST", { url, payload });
    try {
      const response = await axios.post(url, payload, { headers: this.authHeaders(accessToken) });
      console.log("[BillingRecipientService] SUMMARY RESPONSE", {
        status: response.status,
        keys: Object.keys(response.data || {}),
        data: response.data,
      });
      return response.data;
    } catch (error: any) {
      console.error("[BillingRecipientService] SUMMARY ERROR", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      throw error;
    }
  }

  static async getDeliveryStats(accessToken: string, params: Record<string, any> = {}): Promise<DeliveryStatsResponse> {
    const query = new URLSearchParams(params as any).toString();
    const url = `${this.base}/billing-delivery-stats/${query ? "?" + query : ""}`;
    const { data } = await axios.get(url, { headers: this.authHeaders(accessToken) });
    return data;
  }

  static async getDeliveryHistory(accessToken: string, params: Record<string, any> = {}): Promise<DeliveryHistoryResponse> {
    const query = new URLSearchParams(params as any).toString();
    const url = `${this.base}/billing-delivery-history/${query ? "?" + query : ""}`;
    const { data } = await axios.get(url, { headers: this.authHeaders(accessToken) });
    return data;
  }
}

export default BillingRecipientService;
